import { useMemo } from 'react';
import { useInfiniteQuery } from 'react-query';

/**
 *
 * @param {object} args
 * @param {string | Array<any>} args.queryKey
 * @param {function} args.queryFn
 * @param {object} [args.params={}]
 * @param {boolean} [args.enabled=true]
 * @param {number} [args.initialPage=1]
 * @param {function} [args.handleDataFn]
 *
 */

const useInfinitePagination = ({
  queryKey,
  params = {},
  enabled = true,
  initialPage = 1,
  queryFn,
  handleDataFn,
}) => {
  const infiniteQuery = useInfiniteQuery(
    [queryKey, params],
    ({ pageParam = initialPage }) =>
      queryFn({
        ...params,
        page: pageParam,
        pageSize: Number(params.pageSize || 50),
      }),
    {
      enabled,
      getNextPageParam: (lastPage, allPages) => {
        const total = lastPage?.meta?.total;

        if (!lastPage?.data?.length) return undefined;

        const loadedSoFar = allPages.reduce(
          (sum, p) => sum + (p?.data?.length || 0),
          0
        );

        if (loadedSoFar >= total) return undefined;

        return allPages.length + 1;
      },
    }
  );

  const pages = infiniteQuery.data?.pages || [];

  const items = useMemo(() => {
    if (handleDataFn) {
      return handleDataFn(pages);
    }

    return pages.flatMap((p) => p?.data || []);
  }, [pages]); // eslint-disable-line react-hooks/exhaustive-deps

  const total = pages?.[0]?.meta?.total ?? 0;

  return {
    ...infiniteQuery,
    items,
    total,
  };
};

export { useInfinitePagination };
